import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Zap,
  Send,
  Share2,
  CheckCircle,
  Clock,
  User,
  Handshake,
  Play,
  RefreshCw,
  Info,
  HelpCircle,
  X
} from 'lucide-react';
import SimplifyDebtsExplainerModal from './SimplifyDebtsExplainerModal';

export default function GroupBalancesView({
  activeGroup,
  memberBreakdown = [],
  byCurrency = null,
  simplifyDebts = true,
  repaymentsSaved = 0,
  currentUserId,
  currency = 'DH',
  formatMoney,
  isAdmin = false,
  isLoading = false,
  onBack,
  onToggleSimplify,
  onSettleDebt,
  onRemindDebt,
  onRefresh,
  apiClient,
  showNotification
}) {
  const [refreshing, setRefreshing] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [showExplainer, setShowExplainer] = useState(false);
  const [selectedExplanationDebt, setSelectedExplanationDebt] = useState(null);

  // Multi-Currency Partitioning
  const currencyKeys = byCurrency && typeof byCurrency === 'object' ? Object.keys(byCurrency) : [];
  const hasMultipleCurrencies = currencyKeys.length > 1;

  // Active currency selection defaulting to activeGroup?.currency || Object.keys(byCurrency)[0]
  const [activeCurrency, setActiveCurrency] = useState(() => {
    if (activeGroup?.currency && currencyKeys.includes(activeGroup.currency)) {
      return activeGroup.currency;
    }
    return currencyKeys[0] || activeGroup?.currency || currency || 'AED';
  });

  // Ensure activeCurrency remains valid if byCurrency changes
  useEffect(() => {
    if (currencyKeys.length > 0 && !currencyKeys.includes(activeCurrency)) {
      const fallback = (activeGroup?.currency && currencyKeys.includes(activeGroup.currency))
        ? activeGroup.currency
        : currencyKeys[0];
      setActiveCurrency(fallback);
    }
  }, [currencyKeys, activeCurrency, activeGroup]);

  // Derive active partition data
  const currentPartition = (byCurrency && byCurrency[activeCurrency]) ? byCurrency[activeCurrency] : null;
  const currentMemberBreakdown = currentPartition?.member_breakdown || memberBreakdown || [];
  const currentTransfers = currentPartition?.transfers || [];
  const currentRepaymentsSaved = currentPartition ? (currentPartition.repayments_saved ?? 0) : repaymentsSaved;
  const effectiveCurrency = hasMultipleCurrencies ? activeCurrency : (activeGroup?.currency || currency || 'AED');

  // Map of memberId -> boolean indicating whether its subdebt accordion is expanded (default all true)
  const [expandedMembers, setExpandedMembers] = useState(() => {
    const initial = {};
    (currentMemberBreakdown || []).forEach(m => {
      initial[m.user_id] = true;
    });
    return initial;
  });

  const toggleMemberAccordion = (memberId) => {
    setExpandedMembers(prev => ({
      ...prev,
      [memberId]: prev[memberId] === false ? true : false
    }));
  };

  const handleToggleSimplify = async () => {
    if (!isAdmin) {
      if (showNotification) {
        showNotification('Only group admins can toggle debt simplification.', 'warning');
      } else {
        alert('Only group admins can toggle debt simplification.');
      }
      return;
    }
    if (toggling) return;
    setToggling(true);
    try {
      await onToggleSimplify(!simplifyDebts);
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="group-balances-view animate-fade-in" style={{
      minHeight: '100%',
      display: 'flex',
      flexDirection: 'column',
      paddingBottom: '90px'
    }}>
      {/* Top Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '16px 20px',
        background: 'rgba(18, 24, 38, 0.9)',
        backdropFilter: 'blur(16px)',
        borderBottom: '1px solid var(--border-glass)',
        position: 'sticky',
        top: 0,
        zIndex: 20
      }}>
        <button
          type="button"
          onClick={onBack}
          style={{
            background: 'rgba(255, 255, 255, 0.06)',
            border: '1px solid var(--border-glass)',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-main)',
            cursor: 'pointer'
          }}
          title="Back to Group"
        >
          <ArrowLeft size={18} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>Balances</h2>
            <button
              type="button"
              onClick={() => setShowExplainer(true)}
              style={{
                background: 'rgba(0, 245, 212, 0.1)',
                border: '1px solid rgba(0, 245, 212, 0.25)',
                color: 'var(--accent-cyan)',
                borderRadius: '12px',
                padding: '2px 8px',
                fontSize: '11px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}
              title="Learn how Simplify Debts works"
            >
              <HelpCircle size={12} />
              <span>What is this?</span>
            </button>
          </div>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            {activeGroup?.group_name || 'Group'} • {simplifyDebts ? '⚡ Simplified Debts' : '📊 Direct Pairwise'}
          </span>
        </div>
        {onRefresh && (
          <button
            type="button"
            onClick={async () => {
              if (refreshing) return;
              setRefreshing(true);
              try {
                await onRefresh();
              } finally {
                setRefreshing(false);
              }
            }}
            style={{
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid var(--border-glass)',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-muted)',
              cursor: 'pointer'
            }}
            title="Refresh Balances"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          </button>
        )}
      </div>

      {/* Multi-Currency Tab Bar (rendered when byCurrency has >1 currency) */}
      {hasMultipleCurrencies && (
        <div
          className="currency-selector-bar"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            background: 'rgba(18, 24, 38, 0.75)',
            backdropFilter: 'blur(12px)',
            borderBottom: '1px solid var(--border-glass)',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            zIndex: 15
          }}
        >
          {currencyKeys.map(cur => {
            const transferCount = byCurrency[cur]?.transfers?.length ?? 0;
            const isActive = activeCurrency === cur;
            return (
              <button
                key={cur}
                type="button"
                className={isActive ? 'cur-tab-active' : 'cur-tab'}
                onClick={() => setActiveCurrency(cur)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: isActive ? '700' : '500',
                  background: isActive
                    ? 'linear-gradient(135deg, rgba(0, 245, 212, 0.2) 0%, rgba(0, 245, 212, 0.08) 100%)'
                    : 'rgba(255, 255, 255, 0.05)',
                  border: isActive
                    ? '1px solid var(--accent-cyan)'
                    : '1px solid var(--border-glass)',
                  color: isActive ? 'var(--accent-cyan)' : 'var(--text-muted)',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  whiteSpace: 'nowrap',
                  boxShadow: isActive ? '0 2px 10px rgba(0, 245, 212, 0.15)' : 'none'
                }}
              >
                {cur} ({transferCount})
              </button>
            );
          })}
        </div>
      )}

      {/* Member Balances List */}
      <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '14px', flex: 1 }}>
        {isLoading && currentMemberBreakdown.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[1, 2, 3].map(i => (
              <div key={i} className="glass-panel" style={{ padding: '20px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ width: '45%', height: '14px', borderRadius: '6px', background: 'rgba(255,255,255,0.06)' }} />
                  <div style={{ width: '70%', height: '10px', borderRadius: '4px', background: 'rgba(255,255,255,0.04)' }} />
                </div>
              </div>
            ))}
          </div>
        ) : currentMemberBreakdown.length === 0 ? (
          <div className="glass-panel" style={{ padding: '32px 20px', textAlign: 'center', borderRadius: '16px' }}>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>
              {hasMultipleCurrencies ? `No member balances recorded for ${effectiveCurrency}.` : 'No member balances recorded for this group.'}
            </p>
          </div>
        ) : (
          currentMemberBreakdown.map(m => {
            const isExpanded = expandedMembers[m.user_id] !== false;
            const net = m.net_balance;
            const isCreditor = net > 0.005;
            const isDebtor = net < -0.005;
            const debts = m.debts || [];

            return (
              <div
                key={m.user_id}
                className="glass-panel"
                style={{
                  borderRadius: '16px',
                  border: '1px solid var(--border-glass)',
                  background: 'rgba(26, 32, 48, 0.6)',
                  overflow: 'hidden'
                }}
              >
                {/* Header Row: Avatar + Headline */}
                <div
                  onClick={() => toggleMemberAccordion(m.user_id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 16px',
                    cursor: 'pointer',
                    gap: '12px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0, flex: 1 }}>
                    {/* Member Avatar */}
                    {m.avatar_url ? (
                      <img
                        src={apiClient?.getAvatarUrl ? apiClient.getAvatarUrl(m.avatar_url) : m.avatar_url}
                        alt={m.display_name}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: '2px solid rgba(255, 255, 255, 0.1)',
                          flexShrink: 0
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: isCreditor ? 'rgba(0, 245, 212, 0.15)' : isDebtor ? 'rgba(255, 71, 87, 0.15)' : 'rgba(255, 255, 255, 0.08)',
                        color: isCreditor ? 'var(--color-inflow)' : isDebtor ? 'var(--color-outflow)' : 'var(--text-main)',
                        border: '1px solid var(--border-glass)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700',
                        fontSize: '15px',
                        flexShrink: 0
                      }}>
                        {m.display_name.charAt(0).toUpperCase()}
                      </div>
                    )}

                    {/* Member Title */}
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{
                        fontSize: '14px',
                        color: 'var(--text-main)',
                        fontWeight: '600'
                      }}>
                        {isCreditor ? (
                          <>
                            <strong>{m.display_name}</strong> gets back <span style={{ color: 'var(--color-inflow)', fontWeight: '800' }}>{formatMoney(net, effectiveCurrency)}</span> in total
                          </>
                        ) : isDebtor ? (
                          <>
                            <strong>{m.display_name}</strong> owes <span style={{ color: 'var(--color-outflow)', fontWeight: '800' }}>{formatMoney(Math.abs(net), effectiveCurrency)}</span> in total
                          </>
                        ) : (
                          <>
                            <strong>{m.display_name}</strong> is settled up
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '4px' }}
                  >
                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                </div>

                {/* Sub-Debts Accordion Content */}
                {isExpanded && debts.length > 0 && (
                  <div style={{
                    padding: '8px 16px 14px 16px',
                    borderTop: '1px solid rgba(255, 255, 255, 0.04)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                  }}>
                    {debts.map((debt, dIdx) => {
                      const isOwedToMember = debt.type === 'owes_to_member';
                      const payerName = isOwedToMember ? debt.other_name : m.display_name;
                      const payeeName = isOwedToMember ? m.display_name : debt.other_name;
                      const isCurrentUserPayer = isOwedToMember ? debt.other_user_id === currentUserId : m.user_id === currentUserId;
                      const hasRedirectedExplanation = debt.explanation && !debt.explanation.is_direct;

                      return (
                        <div
                          key={dIdx}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '8px 12px',
                            background: 'rgba(255, 255, 255, 0.02)',
                            borderRadius: '12px',
                            border: '1px solid rgba(255, 255, 255, 0.03)',
                            gap: '8px',
                            fontSize: '13px',
                            flexWrap: 'wrap'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1, flexWrap: 'wrap' }}>
                            {/* Small avatar or bullet */}
                            <div style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              background: 'rgba(255, 255, 255, 0.08)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              color: 'var(--text-muted)',
                              flexShrink: 0
                            }}>
                              {isOwedToMember ? debt.other_name.charAt(0).toUpperCase() : m.display_name.charAt(0).toUpperCase()}
                            </div>

                            <span style={{ color: 'var(--text-muted)', minWidth: 0 }}>
                              <strong style={{ color: 'var(--text-main)' }}>{payerName}</strong> owes <span style={{ color: 'var(--color-outflow)', fontWeight: '700' }}>{formatMoney(debt.amount, effectiveCurrency)}</span> to <strong style={{ color: 'var(--text-main)' }}>{payeeName}</strong>
                            </span>

                            {hasRedirectedExplanation && (
                              <span
                                className="simplified-route-pill"
                                role="button"
                                tabIndex={0}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedExplanationDebt({
                                    payerName,
                                    payeeName,
                                    amount: debt.amount,
                                    currency: debt.currency || effectiveCurrency,
                                    explanation: debt.explanation
                                  });
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.stopPropagation();
                                    setSelectedExplanationDebt({
                                      payerName,
                                      payeeName,
                                      amount: debt.amount,
                                      currency: debt.currency || effectiveCurrency,
                                      explanation: debt.explanation
                                    });
                                  }
                                }}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  background: 'rgba(0, 245, 212, 0.12)',
                                  border: '1px solid rgba(0, 245, 212, 0.35)',
                                  borderRadius: '12px',
                                  padding: '2px 8px',
                                  color: 'var(--accent-cyan)',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  flexShrink: 0
                                }}
                                title="Why do I owe this person? Click to view explanation"
                              >
                                <span>⚡ Redirected</span>
                                <Info size={11} style={{ opacity: 0.8 }} />
                              </span>
                            )}
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                            {isCurrentUserPayer ? (
                              <button
                                type="button"
                                onClick={() => onSettleDebt && onSettleDebt(debt, m, effectiveCurrency)}
                                style={{
                                  padding: '5px 12px',
                                  borderRadius: '20px',
                                  fontSize: '12px',
                                  fontWeight: '700',
                                  background: 'linear-gradient(135deg, #ff7a18 0%, #ff5238 100%)',
                                  color: '#fff',
                                  border: 'none',
                                  cursor: 'pointer'
                                }}
                              >
                                Settle
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => onRemindDebt && onRemindDebt(debt, m, effectiveCurrency)}
                                style={{
                                  padding: '4px 12px',
                                  borderRadius: '20px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  background: 'rgba(255, 255, 255, 0.05)',
                                  border: '1px solid rgba(0, 245, 212, 0.4)',
                                  color: 'var(--accent-cyan)',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                Remind...
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Sticky Bottom Simplification Banner */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '12px 20px',
        background: 'rgba(12, 16, 26, 0.95)',
        backdropFilter: 'blur(20px)',
        borderTop: '1px solid var(--border-glass)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: 30,
        gap: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
          <div style={{
            width: '34px',
            height: '34px',
            borderRadius: '10px',
            background: simplifyDebts ? 'rgba(0, 245, 212, 0.15)' : 'rgba(255, 122, 24, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            {simplifyDebts ? <Zap size={18} color="var(--accent-cyan)" /> : <Sparkles size={18} color="#ff7a18" />}
          </div>

          <div style={{ minWidth: 0, flex: 1 }}>
            {simplifyDebts ? (
              <div style={{ fontSize: '12px', color: 'var(--text-main)', fontWeight: '600' }}>
                Simplify debts is on, saving your group <strong style={{ color: 'var(--accent-cyan)' }}>{currentRepaymentsSaved} repayments</strong>.{' '}
                <button
                  type="button"
                  onClick={() => setShowExplainer(true)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--accent-cyan)',
                    padding: 0,
                    fontSize: '12px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    display: 'inline'
                  }}
                >
                  Learn more
                </button>
              </div>
            ) : (
              <div style={{ fontSize: '12px', color: 'var(--text-main)', fontWeight: '600' }}>
                To remind you simplify debts would save your group <strong style={{ color: '#ff7a18' }}>{currentRepaymentsSaved} repayments</strong>.{' '}
                <button
                  type="button"
                  onClick={() => setShowExplainer(true)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#ff7a18',
                    padding: 0,
                    fontSize: '12px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    display: 'inline'
                  }}
                >
                  Learn more
                </button>
              </div>
            )}
          </div>
        </div>

        <div>
          {simplifyDebts ? (
            <button
              type="button"
              disabled={toggling}
              onClick={handleToggleSimplify}
              title={isAdmin ? 'Turn off debt simplification' : 'Only group admins can toggle debt simplification'}
              style={{
                padding: '6px 14px',
                borderRadius: '16px',
                fontSize: '12px',
                fontWeight: '600',
                background: 'rgba(255, 255, 255, 0.08)',
                color: 'var(--text-muted)',
                border: '1px solid var(--border-glass)',
                cursor: isAdmin ? 'pointer' : 'not-allowed',
                opacity: isAdmin ? 1 : 0.6
              }}
            >
              {toggling ? '...' : (isAdmin ? 'Turn off' : 'Turn off (Admin Only)')}
            </button>
          ) : (
            <button
              type="button"
              disabled={toggling}
              onClick={handleToggleSimplify}
              title={isAdmin ? 'Turn on debt simplification' : 'Only group admins can toggle debt simplification'}
              style={{
                padding: '6px 14px',
                borderRadius: '16px',
                fontSize: '12px',
                fontWeight: '700',
                background: isAdmin ? 'linear-gradient(135deg, #ff7a18 0%, #ff5238 100%)' : 'rgba(255, 255, 255, 0.1)',
                color: isAdmin ? '#fff' : 'var(--text-muted)',
                border: 'none',
                cursor: isAdmin ? 'pointer' : 'not-allowed',
                boxShadow: isAdmin ? '0 2px 8px rgba(255, 82, 56, 0.3)' : 'none',
                opacity: isAdmin ? 1 : 0.6
              }}
            >
              {toggling ? '...' : (isAdmin ? 'Turn on now' : 'Turn on now (Admin Only)')}
            </button>
          )}
        </div>
      </div>

      {/* Transitive Debt Explanation Modal */}
      {selectedExplanationDebt && (
        <div
          className="modal-backdrop modal-backdrop-blur animate-fade-in"
          onClick={() => setSelectedExplanationDebt(null)}
          role="presentation"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            background: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(8px)',
            boxSizing: 'border-box'
          }}
        >
          <div
            className="glass-panel modal-spring-enter"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="explanation-dialog-title"
            style={{
              width: '100%',
              maxWidth: '460px',
              background: 'var(--bg-secondary, #121824)',
              borderRadius: '24px',
              padding: '24px',
              border: '1px solid var(--border-glass, rgba(255, 255, 255, 0.08))',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.85)',
              display: 'flex',
              flexDirection: 'column',
              gap: '18px',
              color: 'var(--text-main, #ffffff)',
              boxSizing: 'border-box'
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  background: 'rgba(0, 245, 212, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Zap size={18} color="var(--accent-cyan)" />
                </div>
                <div>
                  <h3 id="explanation-dialog-title" style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                    Why is this debt redirected?
                  </h3>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    Simplified Settlement Route
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedExplanationDebt(null)}
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text-muted)',
                  cursor: 'pointer'
                }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Routing Visual Breakdown */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid var(--border-glass)',
              borderRadius: '16px',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                <span style={{ color: 'var(--text-muted)' }}>Originally Owed:</span>
                <span style={{ fontWeight: '700', color: 'var(--text-main)' }}>
                  {selectedExplanationDebt.explanation?.original_recipient_name || selectedExplanationDebt.explanation?.original_payer_name || 'Original Creditor'}{' '}
                  <span style={{ color: 'var(--color-outflow)' }}>
                    ({formatMoney(selectedExplanationDebt.explanation?.original_amount || selectedExplanationDebt.amount, selectedExplanationDebt.currency)})
                  </span>
                </span>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: 'var(--accent-cyan)',
                fontSize: '11px',
                padding: '6px 10px',
                background: 'rgba(0, 245, 212, 0.08)',
                borderRadius: '8px'
              }}>
                <Zap size={13} />
                <span>Intermediary payment eliminated to simplify group balances</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                <span style={{ color: 'var(--text-muted)' }}>Redirected To:</span>
                <span style={{ fontWeight: '700', color: 'var(--accent-cyan)' }}>
                  {selectedExplanationDebt.payeeName}
                </span>
              </div>
            </div>

            {/* Why Explanation */}
            <div style={{ fontSize: '12.5px', lineHeight: '1.5', color: 'var(--text-muted)' }}>
              <strong style={{ color: 'var(--text-main)', display: 'block', marginBottom: '4px' }}>Why:</strong>
              {selectedExplanationDebt.explanation?.readable_reason ||
               selectedExplanationDebt.explanation?.reason ||
               'This debt was adjusted to eliminate an intermediary payment and reduce the total number of transfers in the group. Your net balance remains mathematically identical down to the cent.'}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
              <button
                type="button"
                onClick={() => {
                  setSelectedExplanationDebt(null);
                  setShowExplainer(true);
                }}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid var(--border-glass)',
                  color: 'var(--text-main)',
                  cursor: 'pointer'
                }}
              >
                Learn More
              </button>
              <button
                type="button"
                onClick={() => setSelectedExplanationDebt(null)}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '700',
                  background: 'var(--accent-cyan)',
                  border: 'none',
                  color: '#0c101a',
                  cursor: 'pointer'
                }}
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visual Explainer Modal */}
      <SimplifyDebtsExplainerModal
        isOpen={showExplainer}
        onClose={() => setShowExplainer(false)}
      />
    </div>
  );
}
